import { formatPhone, type PublicUser } from "@rhema/client";
import {
  Avatar,
  Button,
  Chip,
  DownloadIcon,
  ListRow,
  PhoneIcon,
  ProductMark,
  Sheet,
  TextField,
  UsersIcon,
} from "@rhema/ui";
import { type FormEvent, useState } from "react";
import { useNavigate } from "react-router";
import { UpgradeLink, useFailure } from "../billing/UpgradeLink";
import { toneFor } from "../data/view";
import { chatWithNumber } from "../people/byNumber";
import { FindByEmail } from "../people/FindByEmail";
import { InviteSheet } from "../people/InviteSheet";
import { PeoplePicker } from "../people/PeoplePicker";
import { PhoneField, type PhoneValue, phoneOf } from "../people/PhoneField";
import { ScanButton } from "../people/QrScanner";
import { deviceRegion, type Invitee, useContactSync } from "../people/useContactSync";
import { useQueravelPanel } from "../queravel/panel";
import { useQueravelOffered } from "../queravel/QueravelChat";
import { usePhoneEnabled } from "../session/features";
import { useApi, useClient, useMe } from "../session/session";

/** The most address book entries the invite list shows at once. */
const INVITE_LIMIT = 300;

/** "Message a number": a number field that opens the chat, or offers an invitation. */
function MessageNumber({ onOpen }: { onOpen: (chatId: string) => void }) {
  const api = useApi();
  const client = useClient();
  const me = useMe();
  const [value, setValue] = useState<PhoneValue>(() => ({ region: deviceRegion(), number: "" }));
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setMissing(null);
    const phone = phoneOf(value);
    if (!phone) {
      setError("Check the number. It does not look like a phone number for the country you picked.");
      return;
    }
    setBusy(true);
    try {
      const found = await chatWithNumber(api, client, phone, me.phone);
      if (found.kind === "chat") onOpen(found.chat.id);
      else setMissing(phone);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} aria-label="Message a number" className="mt-4 flex flex-col gap-2">
      <PhoneField label="Message a number" value={value} onChange={setValue} disabled={busy} error={error} />
      <Button
        type="submit"
        variant="tonal"
        disabled={busy || !value.number.trim()}
        icon={<PhoneIcon size={17} />}
      >
        {busy ? "Looking up the number" : "Message this number"}
      </Button>
      {missing && (
        <div role="status" className="flex items-center gap-3 rounded-field bg-surface-header px-3.5 py-2.5">
          <span className="flex-1 text-sm">{formatPhone(missing)} is not on Rhema.</span>
          <Button size="sm" onClick={() => setInviting(true)}>
            Invite to Rhema
          </Button>
        </div>
      )}
      <InviteSheet open={inviting} onClose={() => setInviting(false)} phone={missing ?? undefined} />
    </form>
  );
}

/** People in the phone's address book who are not on Rhema yet. */
function InviteList({ invitees }: { invitees: Invitee[] }) {
  const [picked, setPicked] = useState<Invitee | null>(null);
  if (!invitees.length) return null;
  return (
    <>
      <h3 className="m-0 mt-5 px-1 text-[15px] font-bold">Invite to Rhema</h3>
      <ul aria-label="Invite to Rhema" className="m-0 mt-1 list-none p-0">
        {invitees.slice(0, INVITE_LIMIT).map((p) => (
          <li key={p.phone}>
            <ListRow
              onClick={() => setPicked(p)}
              className="rounded-tile px-2"
              leading={<Avatar name={p.name} tone={toneFor(p.phone)} size={42} />}
              title={p.name}
              subtitle={<span className="truncate">{formatPhone(p.phone)}</span>}
              trailing={<span className="text-sm font-semibold text-blue">Invite</span>}
            />
          </li>
        ))}
      </ul>
      <InviteSheet
        open={picked !== null}
        onClose={() => setPicked(null)}
        name={picked?.name}
        phone={picked?.phone}
      />
    </>
  );
}

type Step = "person" | "group-people" | "group-name";

// Start a direct chat with one person, or a group with several.
export function NewChatSheet({
  open,
  onClose,
  startAsGroup = false,
}: {
  open: boolean;
  onClose: () => void;
  startAsGroup?: boolean;
}) {
  const client = useClient();
  const navigate = useNavigate();
  const queravel = useQueravelOffered();
  const queravelPanel = useQueravelPanel();
  const sync = useContactSync(open);
  const phone = usePhoneEnabled();
  const me = useMe();
  const [step, setStep] = useState<Step>(startAsGroup ? "group-people" : "person");
  const [picked, setPicked] = useState<PublicUser[]>([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const { error, upgrade, fail, clear } = useFailure();

  const close = () => {
    setStep(startAsGroup ? "group-people" : "person");
    setPicked([]);
    setName("");
    clear();
    onClose();
  };

  const openSelf = async () => {
    setBusy(true);
    clear();
    try {
      const chat = await client.selfChat();
      close();
      navigate(`/chats/${chat.id}`);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const openDirect = async (person: PublicUser) => {
    setBusy(true);
    clear();
    try {
      const chat = await client.createDirect(person.id);
      close();
      navigate(`/chats/${chat.id}`);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (person: PublicUser) =>
    setPicked((list) =>
      list.some((p) => p.id === person.id) ? list.filter((p) => p.id !== person.id) : [...list, person],
    );

  const createGroup = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    clear();
    try {
      const chat = await client.createGroup(
        name.trim(),
        picked.map((p) => p.id),
      );
      close();
      navigate(`/chats/${chat.id}`);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const title = step === "person" ? "New chat" : step === "group-people" ? "New group" : "Name the group";
  const subtitle =
    step === "group-people"
      ? picked.length
        ? `${picked.length} ${picked.length === 1 ? "person" : "people"} chosen`
        : "Choose the people to add"
      : step === "group-name"
        ? `With ${picked.map((p) => p.display_name.split(" ")[0]).join(", ")}`
        : undefined;

  return (
    <Sheet open={open} onClose={close} title={title} subtitle={subtitle} fixed className="px-5 pb-7">
      {error && (
        <p role="alert" className="m-0 mt-3 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
          {error}
          {upgrade && <UpgradeLink />}
        </p>
      )}

      {step === "person" && (
        <>
          <Button
            variant="soft"
            block
            className="mt-4"
            icon={<UsersIcon size={18} />}
            onClick={() => setStep("group-people")}
          >
            New group
          </Button>
          <Button
            variant="soft"
            block
            className="mt-2"
            disabled={busy}
            icon={<Avatar name={me.display_name} src={me.avatar_url} tone="self" size={22} />}
            onClick={() => void openSelf()}
          >
            Message yourself
          </Button>
          <Button
            variant="soft"
            block
            className="mt-2"
            icon={<DownloadIcon size={18} />}
            onClick={() => {
              close();
              navigate("/import");
            }}
          >
            Import from WhatsApp
          </Button>
          {queravel && (
            <Button
              variant="soft"
              block
              className="mt-2"
              icon={<ProductMark product="queravel" size={22} />}
              onClick={() => {
                close();
                queravelPanel.show();
              }}
            >
              Queravel
            </Button>
          )}
          {phone ? (
            <>
              <ScanButton onError={fail} onDone={close} />
              <MessageNumber
                onOpen={(chatId) => {
                  close();
                  navigate(`/chats/${chatId}`);
                }}
              />
            </>
          ) : (
            <FindByEmail
              label="Email address"
              action="Message"
              busyText="Looking up the address"
              onFound={(p) => void openDirect(p)}
            />
          )}
          <div aria-busy={busy || sync.syncing}>
            <PeoplePicker
              autoFocus={false}
              onPick={(p) => void openDirect(p)}
              emptyText={
                !phone
                  ? "You have no contacts yet. Enter someone's email address to start a chat."
                  : sync.available
                    ? sync.syncing
                      ? "Checking which of your contacts use Rhema."
                      : "None of your contacts are on Rhema yet. Invite them below."
                    : "You have no contacts on Rhema yet. Message a number to start."
              }
            />
          </div>
          {phone && !sync.available && (
            <p className="m-0 mt-3 px-1 text-[13px] text-ink-secondary">
              Your contacts come from Rhema on your phone, which checks who in your address book uses Rhema.
            </p>
          )}
          {sync.denied && (
            <p className="m-0 mt-3 px-1 text-[13px] text-ink-secondary">
              Rhema cannot see your contacts. Allow access in your phone's settings to see who uses Rhema.
            </p>
          )}
          {sync.error && (
            <p role="alert" className="m-0 mt-3 px-1 text-[13px] text-scarlet">
              {sync.error}
            </p>
          )}
          {phone && <InviteList invitees={sync.invitees} />}
        </>
      )}

      {step === "group-people" && (
        <>
          {picked.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {picked.map((p) => (
                <Chip key={p.id} pressed onClick={() => toggle(p)} aria-label={`Remove ${p.display_name}`}>
                  {p.display_name}
                </Chip>
              ))}
            </div>
          )}
          <PeoplePicker multiple selected={picked.map((p) => p.id)} onPick={toggle} />
          <Button size="lg" block className="mt-4" onClick={() => setStep("group-name")}>
            {picked.length ? "Next" : "Continue without people"}
          </Button>
        </>
      )}

      {step === "group-name" && (
        <form onSubmit={createGroup} className="mt-4 flex flex-col gap-3">
          <TextField
            label="Group name"
            required
            maxLength={80}
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Button type="submit" size="lg" disabled={busy || !name.trim()}>
            {busy ? "Creating the group" : "Create group"}
          </Button>
          <Button variant="ghost" onClick={() => setStep("group-people")} disabled={busy}>
            Back to people
          </Button>
        </form>
      )}
    </Sheet>
  );
}
